import React, {useEffect, useRef, useState} from 'react';
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {ArrowUturnLeftIcon, ArrowUturnRightIcon} from "@heroicons/react/24/outline";
import {Loader} from "../../../loader";

export default function PhotoViewerWrapper({filePath, updateImage, createNewImage, item, hasCrop = false , isResourceLoading}) {

    const cropperRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCropMode, setIsCropMode] = useState(false)
    const [cropSize, setCropSize] = useState({width: 0, height: 0});
    const [rotation, setRotation] = useState(0);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [imageSize, setImageSize] = useState({ width: 400, height: 400 });

    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
            setIsImageLoaded(true);
        };
        img.src = filePath;
    }, [isCropMode, filePath]);


    const applyContainerSize = (w, h, rot) => {
        const rotated = rot % 180 !== 0
            ? { width: h, height: w }
            : { width: w, height: h };

        setImageSize(rotated);
    };

    function fitImageToContainer(cropper) {
        const container = cropper.getContainerData();
        const image = cropper.getImageData();

        const rotated = cropper.getData().rotate % 90 !== 0;
        const naturalW = rotated ? image.naturalHeight : image.naturalWidth;
        const naturalH = rotated ? image.naturalWidth : image.naturalHeight;

        const zoomX = container.width / naturalW;
        const zoomY = container.height / naturalH;

        const zoom = Math.min(zoomX, zoomY);
        cropper.zoomTo(zoom);
    }

    const onCropReady = () => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) return;

        setTimeout(() => {
            const container = cropper.getContainerData();
            const image = cropper.getImageData();

            const zoom = Math.min(
                container.width / image.naturalWidth,
                container.height / image.naturalHeight
            );

            cropper.zoomTo(zoom);

            const canvas = cropper.getCanvasData();
            cropper.setCanvasData({
                ...canvas,
                left: (container.width - canvas.width) / 2,
                top: (container.height - canvas.height) / 2,
            });

            cropper.setCropBoxData({
                width: image.width,
                height: image.height,
                left: 0,
                top: 0,
            });

            setImageSize({width: Math.round(image.naturalWidth), height: Math.round(image.naturalHeight)});
        }, 50);
        setIsLoading(false);
    };

    const onCropResize = () => {
        const cropper = cropperRef.current?.cropper;
        if (cropper) {
            const data = cropper.getData();
            setCropSize({
                width: Math.round(data.width),
                height: Math.round(data.height),
            });
        }
    };

    const handleToggleCropMode = () => {
        setIsCropMode(!isCropMode);
        setIsLoading(true);
    }

    const handleCropAndCreateNew = () => {
        const cropper = cropperRef.current?.cropper;
        if (cropper) {
            let canvas = cropper.getCroppedCanvas();

            if (canvas) {
                canvas.toBlob((blob) => {
                    if (blob) {
                        createNewImage(new File([blob], item.OriginalFilename, {type: 'image/png'}));
                    }
                }, 'image/png');
            }

            handleToggleCropMode();
        }
    };

    const handleUpdateImage = () => {
        const cropper = cropperRef.current?.cropper;
        if (cropper) {
            let canvas = cropper.getCroppedCanvas();

            if (canvas) {
                canvas.toBlob((blob) => {
                    if (blob) {
                        if (updateImage) {
                            updateImage(new File([blob], item.OriginalFilename, {type: 'image/png'}));
                        }
                    }
                }, 'image/png');

                handleToggleCropMode()
            }
        }
    }

    const handleRotate = (rotationStep) => {
        const cropper = cropperRef.current?.cropper;
        const newRotation = (rotation + rotationStep + 360) % 360;
        setRotation(newRotation);

        if (cropper) {
            cropper.rotate(newRotation);
        }

        applyContainerSize(imageSize.width, imageSize.height, rotationStep)

        setTimeout(() => {
            const cropper = cropperRef.current?.cropper;
            if (!cropper) return;

            const container = cropper.getContainerData();

            cropper.clear();
            cropper.reset();
            cropper.rotateTo(newRotation);
            cropper.crop();

            const canvas = cropper.getCanvasData();
            cropper.setCanvasData({
                ...canvas,
                left: 0,
                top: 0
            });

            cropper.setCropBoxData({
                width: container.width,
                height: container.height,
                left: 0,
                top: 0,
            });

            cropper.resize();
            fitImageToContainer(cropper);
        }, 30);
    }

    useEffect(() => {
        if (!isCropMode) return;

        return () => {
            setRotation(0);
            setImageSize({ width: 0, height: 0 });
            setCropSize({ width: 0, height: 0 });

            const cropper = cropperRef.current?.cropper;
            if (cropper && typeof cropper.destroy === 'function') {
                cropper.destroy();
            }

            cropperRef.current = null;
        };
    }, [isCropMode]);

    return (
        <div className="photo-viewer-container" id="pg-photo-container">
            {!!hasCrop && (
                <div style={{marginTop: '1rem'}} className={"flex gap-2 my-2"}>
                    {isCropMode && (
                        <button onClick={handleToggleCropMode} className={"btn btn-outline-dark p-2"}>Cancel edit</button>
                    )}
                    {!isCropMode && (
                        <button onClick={handleToggleCropMode} className={"btn btn-primary p-2"}>Rotate/Crop image</button>
                    )}

                    {isCropMode && (
                        <>
                            <button onClick={handleCropAndCreateNew} className={"btn btn-primary p-2"}>Save and create new version
                            </button>
                            <button onClick={handleUpdateImage} className={"btn btn-primary p-2"}>Save and Replace this image</button>
                            <button onClick={() => handleRotate(-90)} className={"btn btn-primary w-fit"}>
                                <ArrowUturnLeftIcon className={'w-6 h-6'}/>
                            </button>
                            <button onClick={() => handleRotate(90)} className={"btn btn-primary w-fit"}>
                                <ArrowUturnRightIcon className={'w-6 h-6'}/>
                            </button>
                        </>
                    )}
                </div>
            )}


            <div className="relative w-full h-full p-8">
                {!!isLoading && isCropMode && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-50">
                        <Loader/>
                    </div>
                )}

                {!isResourceLoading && filePath && isCropMode && isImageLoaded && (
                    <>
                        <div
                            style={{
                                width: `${imageSize.width}px`,
                                height: `${imageSize.height}px`,
                                position: 'relative',
                                minWidth: '400px',
                                minHeight: '400px'
                            }}>
                            <Cropper
                                src={filePath}
                                style={{ width: '100%', height: '100%' }}
                                    // aspectRatio={1}
                                    guides={true}
                                    cropBoxResizable={true}
                                    cropBoxMovable={true}
                                    dragMode="move"
                                    zoomable={true}
                                    scalable={true}
                                    rotatable={true}
                                    crop={onCropResize}
                                    center={true}
                                    ready={onCropReady}
                                    checkOrientation={false}
                                    autoCropArea={1}
                                    ref={cropperRef}
                                    viewMode={1}
                                />
                        </div>

                        {!isLoading && isCropMode && (
                            <div
                                className="relative top-1 bg-black bg-opacity-80 text-white text-sm px-3 py-1 rounded z-50 font-mono shadow-md flex gap-4 w-fit">
                                <span>Width: {cropSize.width}px</span>
                                <span>Height: {cropSize.height}px</span>
                            </div>
                        )}
                    </>
                )}

                {!isResourceLoading && filePath && !isCropMode && (
                    <img alt="" src={filePath}/>
                )}

                {!isImageLoaded && filePath && !isCropMode && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-50">
                        <Loader/>
                    </div>
                )}
            </div>
        </div>
    );
}
